
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { ResponsiveTabsList, ResponsiveTabsTrigger } from "@/components/admin/ResponsiveTabsComponents";
import AdminsList from "@/components/admin/AdminsList";
import { SystemMembersPanel } from "@/components/admin/SystemMembersPanel";
import SettingsManager from "@/components/admin/SettingsManager";
import ParcelTypesManager from "@/components/admin/ParcelTypesManager";
import OrganizationManager from "@/components/admin/OrganizationManager";
import { useUserRoles } from "@/hooks/useUserRoles";
import LegalContentManager from "@/components/admin/LegalContentManager";
import { EventsManager } from "@/components/admin/events/EventsManager";
import ParcelTypesErrorBoundary from "@/components/admin/ParcelTypesErrorBoundary";
import { Users, Shield, Package, Settings, FileText, Building2, BarChart3, Activity } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";

interface AdminTabsProps {
  defaultTab?: string;
}

const AdminTabs = ({ defaultTab = "dashboard" }: AdminTabsProps) => {
  const { roles } = useUserRoles();
  const isSuperAdmin = roles.platformRole === 'super_admin';
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [activeSettingsTab, setActiveSettingsTab] = useState("general");
  
  const mainTabs = [
    { value: "dashboard", label: "Dashboard", icon: BarChart3 },
    ...(isSuperAdmin ? [{ value: "organizations", label: "Organizations", icon: Building2 }] : []),
    { value: "members", label: "Members", icon: Users },
    { value: "events", label: "Events", icon: Activity },
    { value: "settings", label: "Settings", icon: Settings }
  ];

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
      {/* Mobile: Dropdown Navigation */}
      <div className="block sm:hidden">
        <Select value={activeTab} onValueChange={setActiveTab}>
          <SelectTrigger className="w-full h-12 text-left">
            <div className="flex items-center space-x-2">
              {(() => {
                const currentTab = mainTabs.find(tab => tab.value === activeTab);
                const Icon = currentTab?.icon;
                return (
                  <>
                    {Icon && <Icon className="h-4 w-4" />}
                    <span>{currentTab?.label}</span>
                  </>
                );
              })()}
            </div>
          </SelectTrigger>
          <SelectContent className="z-50">
            {mainTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <SelectItem key={tab.value} value={tab.value}>
                  <div className="flex items-center space-x-2">
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Desktop: Tab Navigation */}
      <div className="hidden sm:block">
        <ResponsiveTabsList className={`grid-cols-2 ${isSuperAdmin ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
          <ResponsiveTabsTrigger
            value="dashboard"
            icon={<BarChart3 className="h-4 w-4" />}
            label="Dashboard"
          />
          {isSuperAdmin && (
            <ResponsiveTabsTrigger
              value="organizations"
              icon={<Building2 className="h-4 w-4" />}
              label="Organizations"
              mobileLabel="Orgs"
            />
          )}
          <ResponsiveTabsTrigger
            value="members"
            icon={<Users className="h-4 w-4" />}
            label="Members"
          />
          <ResponsiveTabsTrigger
            value="events"
            icon={<Activity className="h-4 w-4" />}
            label="Events"
          />
          <ResponsiveTabsTrigger
            value="settings"
            icon={<Settings className="h-4 w-4" />}
            label="Settings"
          />
        </ResponsiveTabsList>
      </div>
      
      <TabsContent value="dashboard" className="space-y-4">
        <div className="bg-white rounded-lg border shadow-sm p-6">
          <h2 className="text-lg font-semibold mb-4">System Dashboard</h2>
          <p className="text-muted-foreground">Dashboard functionality coming soon...</p>
        </div>
      </TabsContent>
      
      {isSuperAdmin && (
        <TabsContent value="organizations" className="space-y-4">
          <OrganizationManager />
        </TabsContent>
      )}
      
      <TabsContent value="members" className="space-y-4">
        <SystemMembersPanel />
      </TabsContent>
      
      <TabsContent value="events" className="space-y-4">
        <EventsManager />
      </TabsContent>
      
      <TabsContent value="settings" className="space-y-4">
        <Tabs value={activeSettingsTab} onValueChange={setActiveSettingsTab} className="w-full">
          <div className="space-y-4">
            {/* Mobile: Settings Dropdown */}
            <div className="block sm:hidden">
              <Select value={activeSettingsTab} onValueChange={setActiveSettingsTab}>
                <SelectTrigger className="w-full h-10">
                  <div className="flex items-center space-x-2">
                    {(() => {
                      const settingsOptions = [
                        { value: "general", label: "General Settings", icon: Settings },
                        { value: "admins", label: "Admin Management", icon: Shield },
                        { value: "parcels", label: "Parcel Types", icon: Package },
                        { value: "legal", label: "Legal Content", icon: FileText }
                      ];
                      const current = settingsOptions.find(opt => opt.value === activeSettingsTab);
                      const Icon = current?.icon;
                      return (
                        <>
                          {Icon && <Icon className="h-4 w-4" />}
                          <span className="text-sm">{current?.label}</span>
                        </>
                      );
                    })()}
                  </div>
                </SelectTrigger>
                <SelectContent className="z-50">
                  <SelectItem value="general">
                    <div className="flex items-center space-x-2">
                      <Settings className="h-4 w-4" />
                      <span>General Settings</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="admins">
                    <div className="flex items-center space-x-2">
                      <Shield className="h-4 w-4" />
                      <span>Admin Management</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="parcels">
                    <div className="flex items-center space-x-2">
                      <Package className="h-4 w-4" />
                      <span>Parcel Types</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="legal">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4" />
                      <span>Legal Content</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Desktop: Tab navigation */}
            <div className="hidden sm:block">
              <ResponsiveTabsList enableScrolling={true}>
                <ResponsiveTabsTrigger
                  value="general"
                  icon={<Settings className="h-4 w-4" />}
                  label="General"
                />
                <ResponsiveTabsTrigger
                  value="admins"
                  icon={<Shield className="h-4 w-4" />}
                  label="Admins"
                />
                <ResponsiveTabsTrigger
                  value="parcels"
                  icon={<Package className="h-4 w-4" />}
                  label="Parcel Types"
                />
                <ResponsiveTabsTrigger
                  value="legal"
                  icon={<FileText className="h-4 w-4" />}
                  label="Legal"
                />
              </ResponsiveTabsList>
            </div>
          </div>
          
          <TabsContent value="general" className="space-y-4 mt-6">
            <SettingsManager />
          </TabsContent>
          
          <TabsContent value="admins" className="space-y-4 mt-6">
            <AdminsList isSuperAdmin={isSuperAdmin} />
          </TabsContent>
          
          <TabsContent value="parcels" className="space-y-4 mt-6">
            <ParcelTypesErrorBoundary>
              <ParcelTypesManager />
            </ParcelTypesErrorBoundary>
          </TabsContent>
          
          <TabsContent value="legal" className="space-y-4 mt-6">
            <LegalContentManager />
          </TabsContent>
        </Tabs>
      </TabsContent>
    </Tabs>
  );
};

export default AdminTabs;
